import SwimFormField from '@/components/shared/form/SwimFormField';
import { Button } from '@/components/ui/button';
import { Card, CardAction, CardContent, CardDescription, CardFooter, CardHeader, CardIcon, CardTitle } from '@/components/ui/card';
import { Form } from '@/components/ui/form';
import { MotionButton } from '@/components/ui/motion-button';
import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute } from '@tanstack/react-router';
import { CardSim } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from "zod";

export const Route = createFileRoute('/debug')({
  component: DebugPage,
})

const enumOptions = [
  "option1", "option2", "option3", "option4", "option5", "option6", "option7", "option8", "option9", "option10",
  "option11", "option12", "option13", "option14", "option15", "option16", "option17", "option18", "option19", "option20"
] as const;

const schema = z.object({
  text: z.string().min(2).max(100),
  number: z.number().min(1).max(100).step(2),
  boolean: z.boolean(),
  date: z.date(),
  enum: z.enum(enumOptions),
  enum_array: z.array(z.enum(enumOptions)),
  file_base64: z.string().optional(),
})

type FormData = z.infer<typeof schema>;

function DebugPage() {
  useEffect(() => {
    document.title = `Identity Verification Debug | Swim Homes`;
  }, []);

  // Helper to fetch PDF and convert to base64
  async function fetchPdfAsBase64(url: string): Promise<string> {
    const res = await fetch(url);
    const blob = await res.blob();
    const arrayBuffer = await blob.arrayBuffer();
    const base64 = btoa(
      String.fromCharCode(...new Uint8Array(arrayBuffer))
    );
    return base64;
  }

  const url = 'https://www.signwell.com/api/v1/documents/';
  const pdfUrl = 'https://www.tug.org/texshowcase/cheat.pdf';

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
  });

  // File upload state
  const [fileName, setFileName] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Handle file upload and convert to base64
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Remove the data:...base64, part if present
      const base64 = result.split(',')[1] || result;
      form.setValue('file_base64', base64, { shouldValidate: true });
    };
    reader.readAsDataURL(file);
  };

  const testFunction = async () => {
    try {
      let file_base64 = form.getValues('file_base64');
      if (!file_base64) {
        // fallback to fetch from URL if not uploaded
        file_base64 = await fetchPdfAsBase64(pdfUrl);
      }
      const options = {
        method: 'POST',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          'X-Api-Key': 'YWNjZXNzOjdkYjZiNzMyM2FjM2RkM2E4MDk5NWU4YjczOTQ1YTEy'
        },
        body: JSON.stringify({
          test_mode: false,
          draft: false,
          with_signature_page: true,
          reminders: true,
          apply_signing_order: false,
          embedded_signing: false,
          embedded_signing_notifications: false,
          text_tags: false,
          allow_decline: true,
          allow_reassign: true,
          files: [{ name: fileName || 'test1', file_base64 }],
          recipients: [{ send_email: false, send_email_delay: 0, id: 'user1', email: 'test@mail.com' }]
        })
      };

      const res = await fetch(url, options);
      const json = await res.json();
      console.log(json);
    } catch (err) {
      console.error(err);
    }
  }

  return (
    <div className="flex gap-2 justify-center items-center w-full min-h-screen">
      <Card className='w-160'>
        <CardHeader>
          <CardTitle>
            Card Data
          </CardTitle>
        </CardHeader>
        <CardContent className='space-y-4'>
          <pre>{JSON.stringify(form.watch(), null, 2)}</pre>
        </CardContent>
      </Card>
      <Form {...form}>
        <Card className='w-160'>
          <CardHeader>
            <CardIcon>
              <CardSim />
            </CardIcon>
            <CardTitle>
              Debug Form
            </CardTitle>
            <CardDescription>
              This is a test
            </CardDescription>
            <CardAction>
              <Button onClick={testFunction}>
                Click me
              </Button>
            </CardAction>
          </CardHeader>
          <CardContent className='space-y-4'>
            <SwimFormField
              schema={schema}
              fieldName="text"
              description="This is a text field with validation"
            />
            <SwimFormField
              schema={schema}
              fieldName="number"
            />
            <SwimFormField
              schema={schema}
              fieldName="boolean"
            />
            <SwimFormField
              schema={schema}
              fieldName="enum"
            />
            <SwimFormField
              schema={schema}
              fieldName="enum_array"
            />
            <SwimFormField
              schema={schema}
              fieldName="date"
            />
            {/* File upload field */}
            <div>
              <label className="block mb-1 font-medium">Upload File (PDF)</label>
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf"
                onChange={handleFileChange}
                className="block"
              />
              {fileName && (
                <div className="text-xs text-muted-foreground mt-1">Selected: {fileName}</div>
              )}
            </div>
          </CardContent>
          <CardFooter className="flex justify-end gap-2">
            <MotionButton
              onClick={async () => {
                const result = await form.trigger();
                console.log(result);
              }}
            >
              verify2
            </MotionButton>
            <MotionButton
              onClick={async () => {
                const result = await form.trigger();
                console.log(result);
              }}
            >
              verify2
            </MotionButton>
          </CardFooter>
        </Card>
      </Form>
    </div>
  );
}
